import { useRef, useState, type ReactNode } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup, EditableSlotGroup, RichTextContent } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { alaViewport } from './alaMotion';

const liveImageDef: SlotGroupDefinition = {
  label: 'Live Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        '',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'American Landmark apartment community',
    },
  },
};

const investImageDef: SlotGroupDefinition = {
  label: 'Invest Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        '',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'American Landmark investment portfolio',
    },
  },
};

const thriveImageDef: SlotGroupDefinition = {
  label: 'Thrive Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        '',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'American Landmark team members',
    },
  },
};

const liveCtaDef: SlotGroupDefinition = {
  label: 'Live CTA',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/our-communities' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Explore communities' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const investCtaDef: SlotGroupDefinition = {
  label: 'Invest CTA',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/investors' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Partner with us' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const thriveCtaDef: SlotGroupDefinition = {
  label: 'Thrive CTA',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/careers' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Join the team' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const SILO_LABEL_CLASS =
  'font-heading text-base font-semibold tabular-nums tracking-wide text-[rgb(var(--ala-secondary))] [text-shadow:0_1px_4px_rgba(0,0,0,0.75)] md:text-lg';

const SILO_LINE_CLASSES = [
  'bg-[rgb(var(--ala-secondary))]',
  'bg-[rgb(var(--ala-accent))]',
  'bg-[#2fb67c]',
];

type SlotData = Record<string, string | null | undefined>;

function SiloPanel({
  index,
  active,
  onActivate,
  image,
  children,
}: {
  index: number;
  active: boolean;
  onActivate: () => void;
  image: ReactNode;
  children: ReactNode;
}) {
  return (
    <motion.div
      role="button"
      tabIndex={0}
      aria-expanded={active}
      onMouseEnter={onActivate}
      onFocus={onActivate}
      onClick={onActivate}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onActivate();
        }
      }}
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={alaViewport}
      transition={{ duration: 0.55, delay: index * 0.1, ease: [0.22, 1, 0.36, 1] }}
      className={`group relative flex w-[84%] shrink-0 snap-center cursor-pointer flex-col overflow-hidden rounded-lg bg-[rgb(var(--ala-dark))] outline-none ring-[rgb(var(--ala-secondary))] transition-[flex-grow] duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] focus-visible:ring-2 md:w-[60%] lg:w-auto lg:shrink lg:basis-0 lg:justify-end lg:bg-transparent ${
        active ? 'lg:grow-[2.8]' : 'lg:grow'
      }`}
    >
      <div className="relative aspect-[16/10] w-full lg:absolute lg:inset-0 lg:aspect-auto">{image}</div>
      <div
        className={`absolute inset-0 hidden bg-gradient-to-t from-black/85 via-black/45 to-black/10 transition-opacity duration-500 lg:block ${
          active ? 'opacity-100' : 'opacity-80'
        }`}
      />
      <div
        className={`absolute inset-x-0 top-0 z-20 h-1 origin-left ${SILO_LINE_CLASSES[index]} transition-transform duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] max-lg:scale-x-100 ${
          active ? 'scale-x-100' : 'scale-x-0'
        }`}
        aria-hidden
      />
      <div className="relative z-10 flex flex-col gap-4 p-6 md:p-8">{children}</div>
    </motion.div>
  );
}

function SiloCta({ data, fallbackUrl, fallbackLabel }: { data: SlotData; fallbackUrl: string; fallbackLabel: string }) {
  return (
    <a
      href={data.url || fallbackUrl}
      target={data._target || '_self'}
      className="inline-flex items-center gap-2 font-heading text-sm font-medium uppercase tracking-wide text-[rgb(var(--ala-on-primary))] transition-colors hover:text-[rgb(var(--ala-secondary))]"
      onClick={(event) => event.stopPropagation()}
    >
      {data.label || fallbackLabel}
      <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" aria-hidden />
    </a>
  );
}

export function HomeAbout() {
  const liveImage = useSlotGroup('homeabout.live_image', liveImageDef);
  const investImage = useSlotGroup('homeabout.invest_image', investImageDef);
  const thriveImage = useSlotGroup('homeabout.thrive_image', thriveImageDef);
  const liveCta = useSlotGroup('homeabout.live_cta', liveCtaDef);
  const investCta = useSlotGroup('homeabout.invest_cta', investCtaDef);
  const thriveCta = useSlotGroup('homeabout.thrive_cta', thriveCtaDef);
  const [activeSilo, setActiveSilo] = useState(0);
  const sliderRef = useRef<HTMLDivElement>(null);
  const bodyReveal = (index: number) =>
    `grid transition-all duration-500 ${
      activeSilo === index ? 'lg:grid-rows-[1fr] lg:opacity-100' : 'lg:grid-rows-[0fr] lg:opacity-0'
    }`;

  const handleSliderScroll = () => {
    const slider = sliderRef.current;
    if (!slider || slider.scrollWidth <= slider.clientWidth) return;
    const cards = Array.from(slider.children) as HTMLElement[];
    const center = slider.scrollLeft + slider.clientWidth / 2;
    let closest = 0;
    cards.forEach((card, index) => {
      const cardCenter = card.offsetLeft + card.offsetWidth / 2;
      const best = cards[closest].offsetLeft + cards[closest].offsetWidth / 2;
      if (Math.abs(cardCenter - center) < Math.abs(best - center)) closest = index;
    });
    if (closest !== activeSilo) setActiveSilo(closest);
  };

  const scrollToSilo = (index: number) => {
    const slider = sliderRef.current;
    const card = slider?.children[index] as HTMLElement | undefined;
    if (!slider || !card || slider.scrollWidth <= slider.clientWidth) return;
    slider.scrollTo({
      left: card.offsetLeft - (slider.clientWidth - card.offsetWidth) / 2,
      behavior: 'smooth',
    });
  };

  const activateSilo = (index: number) => {
    setActiveSilo(index);
    scrollToSilo(index);
  };

  return (
    <section className="flex w-full justify-center bg-[rgb(var(--ala-surface))] px-4 pb-20 pt-6 md:px-8 md:pb-32 md:pt-10">
      <div className="flex w-full max-w-[1440px] flex-col items-center">
        <div className="w-full">
        <div
          ref={sliderRef}
          onScroll={handleSliderScroll}
          className="-mx-4 flex snap-x snap-mandatory gap-3 overflow-x-auto overscroll-x-contain px-4 [-ms-overflow-style:none] [scrollbar-width:none] md:-mx-8 md:gap-4 md:px-8 lg:mx-0 lg:h-[520px] lg:snap-none lg:overflow-visible lg:px-0 [&::-webkit-scrollbar]:hidden"
        >
          <SiloPanel
            index={0}
            active={activeSilo === 0}
            onActivate={() => activateSilo(0)}
            image={
              <EditableSlotGroup
                groupPath="homeabout.live_image"
                definition={liveImageDef}
                className="block h-full w-full"
              >
                <VibesitesImage
                  url={
                    liveImage.image ||
                    ''
                  }
                  alt={liveImage.alt || ''}
                  sizes="(max-width: 1024px) 100vw, 55vw"
                  className="h-full w-full object-cover transition-transform duration-[1.2s] group-hover:scale-105"
                />
              </EditableSlotGroup>
            }
          >
            <span className={SILO_LABEL_CLASS}>
              01 — Live
            </span>
            <RichTextContent
              path="homeabout.live_title"
              label="Live Title"
              as="h3"
              className="font-heading text-[clamp(28px,3.2vw,48px)] font-medium leading-[1.05] tracking-[-0.04em] text-[rgb(var(--ala-on-primary))]"
            />
            <div className={bodyReveal(0)}>
              <div className="overflow-hidden">
                <div className="flex flex-col gap-5 pt-1">
                  <RichTextContent
                    path="homeabout.live_body"
                    label="Live Body"
                    as="p"
                    className="max-w-[420px] font-heading text-sm leading-relaxed tracking-[-0.2px] text-[rgb(var(--ala-on-primary))]/85 md:text-base"
                  />
                  <EditableSlotGroup groupPath="homeabout.live_cta" definition={liveCtaDef}>
                    <SiloCta data={liveCta} fallbackUrl="/our-communities" fallbackLabel="Explore communities" />
                  </EditableSlotGroup>
                </div>
              </div>
            </div>
          </SiloPanel>

          <SiloPanel
            index={1}
            active={activeSilo === 1}
            onActivate={() => activateSilo(1)}
            image={
              <EditableSlotGroup
                groupPath="homeabout.invest_image"
                definition={investImageDef}
                className="block h-full w-full"
              >
                <VibesitesImage
                  url={
                    investImage.image ||
                    ''
                  }
                  alt={investImage.alt || ''}
                  sizes="(max-width: 1024px) 100vw, 55vw"
                  className="h-full w-full object-cover transition-transform duration-[1.2s] group-hover:scale-105"
                />
              </EditableSlotGroup>
            }
          >
            <span className={SILO_LABEL_CLASS}>
              02 — Invest
            </span>
            <RichTextContent
              path="homeabout.invest_title"
              label="Invest Title"
              as="h3"
              className="font-heading text-[clamp(28px,3.2vw,48px)] font-medium leading-[1.05] tracking-[-0.04em] text-[rgb(var(--ala-on-primary))]"
            />
            <div className={bodyReveal(1)}>
              <div className="overflow-hidden">
                <div className="flex flex-col gap-5 pt-1">
                  <RichTextContent
                    path="homeabout.invest_body"
                    label="Invest Body"
                    as="p"
                    className="max-w-[420px] font-heading text-sm leading-relaxed tracking-[-0.2px] text-[rgb(var(--ala-on-primary))]/85 md:text-base"
                  />
                  <EditableSlotGroup groupPath="homeabout.invest_cta" definition={investCtaDef}>
                    <SiloCta data={investCta} fallbackUrl="/investors" fallbackLabel="Partner with us" />
                  </EditableSlotGroup>
                </div>
              </div>
            </div>
          </SiloPanel>

          <SiloPanel
            index={2}
            active={activeSilo === 2}
            onActivate={() => activateSilo(2)}
            image={
              <EditableSlotGroup
                groupPath="homeabout.thrive_image"
                definition={thriveImageDef}
                className="block h-full w-full"
              >
                <VibesitesImage
                  url={
                    thriveImage.image ||
                    ''
                  }
                  alt={thriveImage.alt || ''}
                  sizes="(max-width: 1024px) 100vw, 55vw"
                  className="h-full w-full object-cover transition-transform duration-[1.2s] group-hover:scale-105"
                />
              </EditableSlotGroup>
            }
          >
            <span className={SILO_LABEL_CLASS}>
              03 — Thrive
            </span>
            <RichTextContent
              path="homeabout.thrive_title"
              label="Thrive Title"
              as="h3"
              className="font-heading text-[clamp(28px,3.2vw,48px)] font-medium leading-[1.05] tracking-[-0.04em] text-[rgb(var(--ala-on-primary))]"
            />
            <div className={bodyReveal(2)}>
              <div className="overflow-hidden">
                <div className="flex flex-col gap-5 pt-1">
                  <RichTextContent
                    path="homeabout.thrive_body"
                    label="Thrive Body"
                    as="p"
                    className="max-w-[420px] font-heading text-sm leading-relaxed tracking-[-0.2px] text-[rgb(var(--ala-on-primary))]/85 md:text-base"
                  />
                  <EditableSlotGroup groupPath="homeabout.thrive_cta" definition={thriveCtaDef}>
                    <SiloCta data={thriveCta} fallbackUrl="/careers" fallbackLabel="Join the team" />
                  </EditableSlotGroup>
                </div>
              </div>
            </div>
          </SiloPanel>
        </div>
        <div className="mt-5 flex items-center justify-center gap-2 lg:hidden">
          {['Live', 'Invest', 'Thrive'].map((label, index) => (
            <button
              key={label}
              type="button"
              aria-label={`Show ${label}`}
              aria-current={activeSilo === index}
              onClick={() => activateSilo(index)}
              className={`h-2 rounded-full transition-all duration-300 ${
                activeSilo === index
                  ? `w-6 ${SILO_LINE_CLASSES[index]}`
                  : 'w-2 bg-[rgb(var(--ala-text-muted))]/40'
              }`}
            />
          ))}
        </div>
        </div>
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'HomeAbout',
  displayName: 'Home About',
  tokens: {
    'homeabout.live_title': {
      label: 'Live Title',
      type: 'richtext',
      defaultValue: '<p>Places people love to call home.</p>',
    },
    'homeabout.live_body': {
      label: 'Live Body',
      type: 'richtext',
      defaultValue:
        '<p>From Landmark360® benefits to communities across the Sunbelt, we design resident experiences that feel thoughtful, responsive, and genuinely livable.</p>',
    },
    'homeabout.invest_title': {
      label: 'Invest Title',
      type: 'richtext',
      defaultValue: '<p>Value created with intent.</p>',
    },
    'homeabout.invest_body': {
      label: 'Invest Body',
      type: 'richtext',
      defaultValue:
        '<p>A vertically integrated owner-operator built for disciplined acquisitions, hands-on repositioning, and durable, risk-adjusted returns for our partners.</p>',
    },
    'homeabout.thrive_title': {
      label: 'Thrive Title',
      type: 'richtext',
      defaultValue: '<p>Careers with room to grow.</p>',
    },
    'homeabout.thrive_body': {
      label: 'Thrive Body',
      type: 'richtext',
      defaultValue:
        '<p>We invest in people as seriously as assets — with culture, advancement, and purpose that help teammates build lasting careers.</p>',
    },
  },
  slotGroups: {
    'homeabout.live_image': liveImageDef,
    'homeabout.invest_image': investImageDef,
    'homeabout.thrive_image': thriveImageDef,
    'homeabout.live_cta': liveCtaDef,
    'homeabout.invest_cta': investCtaDef,
    'homeabout.thrive_cta': thriveCtaDef,
  },
};

(HomeAbout as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default HomeAbout;
